import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { endpoints, urlApiClientes, urlApiLaserchip } from 'src/environments/endpoints';
import {
  AtualizarTokenSessaoRequest,
  AtualizarTokenSessaoResponse,
  IdentificarRequest,
  IdentificarResponse,
  LoginExternoResumidoResponse,
  LoginRequest,
  LoginResponse,
} from '../../models/auth.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /**
   * CONFIRMADO testando ao vivo (24/08/2026): `PossuiSenhaExterna` sem
   * nenhum header `Authorization` responde 400 com a MESMA mensagem de
   * "token inválido" já vista em `Administradora/ParametrosCompletos` sem o
   * `partnerToken` — ou seja, este host também exige o mesmo JWT estático de
   * parceiro antes do usuário ter uma sessão própria (`AuthInterceptor` só
   * injeta o token de SESSÃO, que ainda não existe nesta etapa do login).
   */
  private readonly partnerHeaders = new HttpHeaders({
    Authorization: `Bearer ${environment.partnerToken}`,
  });

  constructor(private http: HttpClient) {}

  /**
   * PASSO 1 do fluxo de login: CPF/CNPJ titular + data de nascimento, botão
   * "Próximo". Endpoint CONFIRMADO via rede real (tenant conectaplan,
   * 24/08/2026) — ver comentário completo em core/models/auth.model.ts.
   */
  possuiSenhaExterna(request: IdentificarRequest): Observable<IdentificarResponse> {
    return this.http.post<IdentificarResponse>(
      `${urlApiClientes}${endpoints.clientes.possuiSenhaExterna}`,
      request,
      { headers: this.partnerHeaders }
    );
  }

  /**
   * NÃO USADO no fluxo implementado hoje (ver `identificarAttempt$` em
   * core/store/effects.ts) — testamos chamar com o mesmo corpo de
   * `possuiSenhaExterna` (sem senha, ainda no passo 1) e o backend
   * respondeu 400 "A senha é obrigatória" (26/08/2026, homologação), ou
   * seja, o corpo/gatilho real são diferentes do que assumimos. Mantido
   * aqui só como referência do endpoint confirmado existir.
   * TODO: confirmar o contrato/gatilho real antes de usar.
   */
  loginExternoResumido(request: IdentificarRequest): Observable<LoginExternoResumidoResponse> {
    return this.http.post<LoginExternoResumidoResponse>(
      `${urlApiClientes}${endpoints.clientes.loginExternoResumido}`,
      request,
      { headers: this.partnerHeaders }
    );
  }

  /**
   * CONFIRMADO em 26/08/2026 (homologação): último passo do PASSO 1, ainda
   * com o `partnerToken` — devolve o token de SESSÃO de curta duração que
   * `loginExterno` (passo 2) precisa usar (ver core/models/auth.model.ts).
   * Host `urlApiLaserchip` (mesmo de Administradora/Parametros*). Corpo
   * `{ chave, tipo }` CONFIRMADO via rede real — `chave` é o mesmo
   * documento do passo 1 (`sCPF`), `tipo` observado como `0`.
   */
  atualizarTokenSessao(request: AtualizarTokenSessaoRequest): Observable<AtualizarTokenSessaoResponse> {
    return this.http.post<AtualizarTokenSessaoResponse>(
      `${urlApiLaserchip}${endpoints.token.atualizarTokenSessao}`,
      request,
      { headers: this.partnerHeaders }
    );
  }

  /**
   * PASSO 2 do fluxo de login: campo de senha, botão "Entrar". Endpoint e
   * CONTRATO DE REQUEST CONFIRMADOS via rede real (produção, tenant
   * conectaplan, 25/08/2026) — ver comentário completo de `LoginRequest` em
   * core/models/auth.model.ts.
   *
   * CONFIRMADO em 26/08/2026 (homologação): usa o token de SESSÃO devolvido
   * por `atualizarTokenSessao()`, NÃO o `partnerToken` estático — mandar o
   * `partnerToken` aqui resulta em HTTP 401 "Usuário sem permissão de
   * acesso". Por isso, diferente dos outros métodos deste serviço, o token
   * é um parâmetro explícito em vez de vir de `partnerHeaders`.
   */
  loginExterno(request: LoginRequest, tokenSessao: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${urlApiClientes}${endpoints.clientes.loginExterno}`,
      request,
      { headers: new HttpHeaders({ Authorization: `Bearer ${tokenSessao}` }) }
    );
  }

  /**
   * Não há nenhum endpoint de logout confirmado ainda (o fluxo pós-login não
   * foi analisado). Por ora, `signOut` só existe para manter a simetria com
   * o AuthService do ARQUITETURA.md (portal-cliente-frontend) — a limpeza de
   * sessão em si é feita pelo reducer ao tratar `CoreActions.signOut`.
   * TODO: confirmar se existe uma chamada de API associada ao logout.
   */
  signOut(): void {
    // Nada a fazer por enquanto — ver comentário acima.
  }
}
